import {
  type CellImageDraw,
  type FoGesrTextColumnLayout,
  type FoGesrTableDrawContext,
  computeFoGesrParticipantImageRowHeight,
  drawFoGesrTableImageCell,
  drawFoGesrTextColumn,
  ensureFoGesrTableRowFitsPage,
  tryResolveCellImageDraw,
} from '../infrastructure/foGesrTableDrawingPrimitives.ts';
import { MAX_ACUDIENTE_DOC_STACK_MM, MAX_ACUDIENTE_DOC_STACK_TOTAL_MM, MAX_FIRMA_CELL_MM, MAX_FOTO_CELL_MM } from '../infrastructure/resolveImageForPdf.ts';

export type TableDrawContext = FoGesrTableDrawContext;

export { drawFoGesrTableTextRow as drawTextRow } from '../infrastructure/foGesrTableDrawingPrimitives.ts';

const ACUDIENTE_IMG_GAP_MM = 0.65;

type PersonaAutorizadaColumnDrawArgs = {
  ctx: TableDrawContext;
  cells: string[];
  colIdx: 4;
  x: number;
  yTop: number;
  rowH: number;
  wi: number;
  fs: number;
  acudienteDraws: CellImageDraw[];
};
/** Draw persona autorizada column with images. */


async function drawPersonaAutorizadaColumnWithImages(args: PersonaAutorizadaColumnDrawArgs): Promise<number> {
  const { ctx, cells, colIdx, x, yTop, rowH, wi, fs, acudienteDraws } = args;
  const { doc, lh, pad, cellLines } = ctx;
  doc.rect(x, yTop, wi, rowH);
  const lines = cellLines(cells[colIdx] ?? '', colIdx, fs);
  let yy = yTop + lh + pad * 0.35;
  for (const ln of lines) {
    doc.text(ln, x + pad, yy, { maxWidth: wi - pad * 2 });
    yy += lh;
  }
  if (acudienteDraws.length > 0) {
    yy += 0.35;
    for (const d of acudienteDraws) {
      const ix = x + pad + (wi - pad * 2 - d.dw) / 2;
      await doc.addImage(d.data, d.fmt, ix, yy, d.dw, d.dh);
      yy += d.dh + ACUDIENTE_IMG_GAP_MM;
    }
  }
  return x + wi;
}
/** Draw participant image row. */


export async function drawParticipantImageRow(
  ctx: TableDrawContext,
  cells: string[],
  registroFotografico: string,
  firma: string,
  personaAutorizadaImagenes: string[] = [],
): Promise<void> {
  const { doc, y, margin, w, lh, pad, setNormal, cellLines } = ctx;
  const fs = 6;
  setNormal(fs);

  const w4 = w[4] ?? 0;
  const w7 = w[7] ?? 0;
  const w8 = w[8] ?? 0;
  const fotoDraw = await tryResolveCellImageDraw(registroFotografico, w7 - pad * 2, MAX_FOTO_CELL_MM);
  const firmaDraw = await tryResolveCellImageDraw(firma, w8 - pad * 2, MAX_FIRMA_CELL_MM);

  const acudienteSources = personaAutorizadaImagenes
    .map((src) => String(src ?? '').trim())
    .filter((src) => src.length > 0);
  const perAcudienteMaxH =
    acudienteSources.length > 0
      ? Math.min(
          MAX_ACUDIENTE_DOC_STACK_MM,
          MAX_ACUDIENTE_DOC_STACK_TOTAL_MM / acudienteSources.length,
        )
      : MAX_ACUDIENTE_DOC_STACK_MM;
  const acudienteDraws: CellImageDraw[] = [];
  for (const src of acudienteSources) {
    const d = await tryResolveCellImageDraw(src, w4 - pad * 2, perAcudienteMaxH);
    if (d) acudienteDraws.push(d);
  }
  const useAcudienteStack = acudienteDraws.length > 0;

  const textColsIdx = [0, 1, 2, 3, 4, 5, 6, 9, 10, 11] as const;

  let rowH: number;
  if (useAcudienteStack) {
    const lineCounts = textColsIdx.map((i) => Math.max(cellLines(cells[i] ?? '', i, fs).length, 1));
    const linesNeeded = Math.max(...lineCounts);
    const textH = linesNeeded * lh + pad * 2;
    const lines4 = Math.max(cellLines(cells[4] ?? '', 4, fs).length, 1);
    const stackH =
      acudienteDraws.reduce((s, d) => s + d.dh + ACUDIENTE_IMG_GAP_MM, 0) - ACUDIENTE_IMG_GAP_MM + pad * 1.1;
    const col4Min = lines4 * lh + pad * 1.35 + stackH;
    const fotoCellInner = fotoDraw ? fotoDraw.dh : lh;
    const firmaCellInner = firmaDraw ? firmaDraw.dh : lh;
    rowH = Math.max(textH, col4Min, fotoCellInner + pad * 2, firmaCellInner + pad * 2);
  } else {
    rowH = computeFoGesrParticipantImageRowHeight({
      cellLines,
      cells,
      textColIndices: textColsIdx,
      fs,
      lh,
      pad,
      fotoDraw,
      firmaDraw,
    });
  }

  ensureFoGesrTableRowFitsPage(ctx, rowH);

  const yTop = y.current;
  let x = margin;
  for (let i = 0; i < 7; i += 1) {
    if (i === 4 && useAcudienteStack) {
      x = await drawPersonaAutorizadaColumnWithImages({
        ctx,
        cells,
        colIdx: 4,
        x,
        yTop,
        rowH,
        wi: w4,
        fs,
        acudienteDraws,
      });
    } else {
      const layout: FoGesrTextColumnLayout = { colIdx: i, x, yTop, rowH, wi: w[i] ?? 0, fs };
      x = drawFoGesrTextColumn(ctx, cells, layout);
    }
  }

  const wi7 = w[7] ?? 0;
  doc.rect(x, yTop, wi7, rowH);
  await drawFoGesrTableImageCell(doc, x, yTop, wi7, pad, lh, fotoDraw);
  x += wi7;

  const wi8 = w[8] ?? 0;
  doc.rect(x, yTop, wi8, rowH);
  await drawFoGesrTableImageCell(doc, x, yTop, wi8, pad, lh, firmaDraw);
  x += wi8;

  for (let i = 9; i < 12; i += 1) {
    const layout: FoGesrTextColumnLayout = { colIdx: i, x, yTop, rowH, wi: w[i] ?? 0, fs };
    x = drawFoGesrTextColumn(ctx, cells, layout);
  }

  y.current += rowH;
  setNormal(6);
}
